// src/lib/envValidation.ts
// Environment variable validation for security with comprehensive TypeScript interfaces

// Environment validation result interface
export interface EnvironmentValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  environment: EnvironmentInfo;
}

// Environment information interface
export interface EnvironmentInfo {
  isDevelopment: boolean;
  isProduction: boolean;
  supabaseUrl: string;
  hasValidConfig: boolean;
}

// Security context validation result
export interface SecurityValidationResult {
  isSecure: boolean;
  errors: string[];
  warnings: string[];
  context: SecurityContext;
}

// Security context interface
export interface SecurityContext {
  isSecureContext: boolean;
  protocol: string;
  hostname: string;
  hasCSP: boolean;
  availableAPIs: Record<string, boolean>;
}

// Complete security check result
export interface SecurityCheckResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  environment: EnvironmentInfo;
  security: SecurityContext;
  summary: SecuritySummary;
}

// Security check summary
export interface SecuritySummary {
  totalErrors: number;
  totalWarnings: number;
  environmentOk: boolean;
  securityOk: boolean;
  overallStatus: 'PASS' | 'FAIL';
}

// Safe environment info interface
export interface SafeEnvironmentInfo {
  mode: string;
  isDev: boolean;
  isProd: boolean;
  hasSupabaseUrl: boolean;
  hasSupabaseKey: boolean;
  supabaseDomain: string;
  timestamp: string;
}

// Required Web APIs interface
interface RequiredAPIs {
  'Crypto API': boolean;
  'Fetch API': boolean;
  'Local Storage': boolean;
  'Session Storage': boolean;
}

// Environment variable names
type RequiredEnvVar = 'VITE_SUPABASE_URL' | 'VITE_SUPABASE_ANON_KEY';

// Note: Vite's import.meta.env types are handled by Vite's built-in types

/**
 * Validates required environment variables on app startup
 */
export const validateEnvironment = (): EnvironmentValidationResult => {
  const errors: string[] = [];
  const requiredVars: RequiredEnvVar[] = ['VITE_SUPABASE_URL', 'VITE_SUPABASE_ANON_KEY'];

  // Check if required variables exist
  for (const varName of requiredVars) {
    const value = import.meta.env[varName];

    if (!value) {
      errors.push(`Missing required environment variable: ${varName}`);
      continue;
    }

    // Validate Supabase URL format
    if (varName === 'VITE_SUPABASE_URL') {
      if (!isValidSupabaseUrl(value)) {
        errors.push(`Invalid Supabase URL format: ${varName}`);
      }
    }

    // Validate Supabase key format
    if (varName === 'VITE_SUPABASE_ANON_KEY') {
      if (!isValidSupabaseKey(value)) {
        errors.push(`Invalid Supabase key format: ${varName}`);
      }
    }
  }

  // Check for development vs production environment
  const isDevelopment = import.meta.env.DEV;
  const isProduction = import.meta.env.PROD;

  if (!isDevelopment && !isProduction) {
    errors.push('Unable to determine environment mode');
  }

  // Warn about potentially unsafe configurations
  const warnings: string[] = [];

  if (isDevelopment) {
    console.warn('🔧 Running in development mode');
  } else {
    console.warn('🚀 Running in production mode');

    // Production-specific validations
    if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
      warnings.push('Production app should be served over HTTPS');
    }
  }

  // Log warnings but don't fail validation
  warnings.forEach(warning => {
    console.warn('⚠️ Security Warning:', warning);
  });

  const environment: EnvironmentInfo = {
    isDevelopment,
    isProduction,
    supabaseUrl: import.meta.env.VITE_SUPABASE_URL || '',
    hasValidConfig: errors.length === 0,
  };

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    environment,
  };
};

/**
 * Validates Supabase URL format
 */
function isValidSupabaseUrl(url: string): boolean {
  if (!url || typeof url !== 'string') {
    return false;
  }

  try {
    const urlObj = new URL(url);

    // Should be HTTPS
    if (urlObj.protocol !== 'https:') {
      return false;
    }

    // Should match Supabase domain pattern
    const { hostname } = urlObj;
    if (!hostname.includes('supabase.co') && !hostname.includes('supabase.in')) {
      return false;
    }

    return true;
  } catch {
    return false;
  }
}

/**
 * Validates Supabase anon key format
 */
function isValidSupabaseKey(key: string): boolean {
  if (!key || typeof key !== 'string') {
    return false;
  }

  // Supabase keys are typically long JWT tokens
  if (key.length < 100) {
    return false;
  }

  // Should start with 'eyJ' (JWT header)
  if (!key.startsWith('eyJ')) {
    return false;
  }

  // Should contain dots (JWT structure)
  if (!key.includes('.')) {
    return false;
  }

  return true;
}

/**
 * Gets safe environment info for logging/debugging
 */
export const getSafeEnvInfo = (): SafeEnvironmentInfo => {
  const { env } = import.meta;

  return {
    mode: env.MODE,
    isDev: env.DEV,
    isProd: env.PROD,
    hasSupabaseUrl: Boolean(env.VITE_SUPABASE_URL),
    hasSupabaseKey: Boolean(env.VITE_SUPABASE_ANON_KEY),
    supabaseDomain: env.VITE_SUPABASE_URL
      ? new URL(env.VITE_SUPABASE_URL).hostname
      : 'not configured',
    timestamp: new Date().toISOString(),
  };
};

/**
 * Validates that we're running in a secure context
 */
export const validateSecurityContext = (): SecurityValidationResult => {
  const warnings: string[] = [];
  const errors: string[] = [];

  // Check for secure context (HTTPS or localhost)
  if (!window.isSecureContext) {
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      warnings.push('Running on localhost without HTTPS (acceptable for development)');
    } else {
      errors.push('App must be served over HTTPS in production');
    }
  }

  // Check for required Web APIs
  const requiredAPIs: RequiredAPIs = {
    'Crypto API': 'crypto' in window && 'getRandomValues' in crypto,
    'Fetch API': 'fetch' in window,
    'Local Storage': 'localStorage' in window,
    'Session Storage': 'sessionStorage' in window,
  };

  Object.entries(requiredAPIs).forEach(([apiName, isAvailable]) => {
    if (!isAvailable) {
      errors.push(`Missing required API: ${apiName}`);
    }
  });

  // Check for CSP headers
  const metaTags = document.querySelectorAll('meta[http-equiv="Content-Security-Policy"]');
  if (metaTags.length === 0) {
    warnings.push('No Content Security Policy detected');
  }

  const context: SecurityContext = {
    isSecureContext: window.isSecureContext,
    protocol: window.location.protocol,
    hostname: window.location.hostname,
    hasCSP: metaTags.length > 0,
    availableAPIs: Object.fromEntries(
      Object.entries(requiredAPIs)
        .filter(([, available]) => available)
        .map(([name]) => [name, true])
    ),
  };

  return {
    isSecure: errors.length === 0,
    errors,
    warnings,
    context,
  };
};

/**
 * Runs all environment and security validations
 */
export const runSecurityChecks = (): SecurityCheckResult => {
  const envValidation = validateEnvironment();
  const securityValidation = validateSecurityContext();

  const allErrors = [...envValidation.errors, ...securityValidation.errors];
  const allWarnings = [...envValidation.warnings, ...securityValidation.warnings];

  const summary: SecuritySummary = {
    totalErrors: allErrors.length,
    totalWarnings: allWarnings.length,
    environmentOk: envValidation.isValid,
    securityOk: securityValidation.isSecure,
    overallStatus: allErrors.length === 0 ? 'PASS' : 'FAIL',
  };

  const result: SecurityCheckResult = {
    isValid: allErrors.length === 0,
    errors: allErrors,
    warnings: allWarnings,
    environment: envValidation.environment,
    security: securityValidation.context,
    summary,
  };

  // Log results
  if (result.isValid) {
    console.warn('✅ Security checks passed');
  } else {
    console.error('❌ Security checks failed:', allErrors);
  }

  if (allWarnings.length > 0) {
    console.warn('⚠️ Security warnings:', allWarnings);
  }

  return result;
};

// Environment validation constants
export const ENV_VALIDATION_CONSTANTS = {
  REQUIRED_VARS: ['VITE_SUPABASE_URL', 'VITE_SUPABASE_ANON_KEY'] as const,
  SUPABASE: {
    URL_PROTOCOL: 'https:',
    ALLOWED_DOMAINS: ['supabase.co', 'supabase.in'],
    JWT_PREFIX: 'eyJ',
    MIN_KEY_LENGTH: 100,
  },
  SECURITY: {
    REQUIRED_APIS: ['crypto', 'fetch', 'localStorage', 'sessionStorage'] as const,
    SECURE_PROTOCOLS: ['https:', 'http:'] as const,
    LOCAL_HOSTNAMES: ['localhost', '127.0.0.1'] as const,
  },
} as const;

// Type guards and utility functions
export const EnvironmentUtils = {
  /**
   * Type guard to check if running in development
   */
  isDevelopment(): boolean {
    return import.meta.env.DEV;
  },

  /**
   * Type guard to check if running in production
   */
  isProduction(): boolean {
    return import.meta.env.PROD;
  },

  /**
   * Get current environment mode
   */
  getMode(): string {
    return import.meta.env.MODE;
  },

  /**
   * Check if all required environment variables are present
   */
  hasRequiredEnvVars(): boolean {
    return ENV_VALIDATION_CONSTANTS.REQUIRED_VARS.every(varName =>
      Boolean(import.meta.env[varName])
    );
  },

  /**
   * Get environment variable safely with fallback
   */
  getEnvVar(name: RequiredEnvVar, fallback: string = ''): string {
    return import.meta.env[name] || fallback;
  },
};
